<template>
  <div class="UploadDashboard d-flex flex-column w-100 align-items-center">
    <div class="ImportPanelWrapper mt-5 mb-4 flex-grow-1">
      <header class="PanelHeader d-flex justify-content-between py-3 px-4 align-items-center">
        {{ $t('components.import.ImportPanel.title') }}
        <div class="ml-auto">
          {{ $t('components.import.ImportPanel.trial') }}
          <ToggleSwitch
            v-model="dryRun"
            class="m-0"
          />
        </div>
      </header>
      <section
        v-if="DeploymentCode==='nl'"
        class="ImportPanel d-flex flex-column p-4"
      >
        <!-- GeoJSON Upload (new standalone section styling) -->
        <div class="d-flex flex-column p-4">
          <div class="AdminFormSection mt-3 pb-3">
            <div class="AdminFormSection__Header mb-4">
              <h3 class="AdminFormSection__Title">
                {{ $t('components.import.ImportPanel.geojson.title') }}
              </h3>
              <p class="AdminFormSection__Description">
                {{ $t('components.import.ImportPanel.geojson.description') }}
              </p>
            </div>

            <div class="AdminFormSection__Body">
              <!-- Import Name -->
              <div class="AdminFormSection__Row mb-2">
                <label class="AdminFormSection__Label geojson">{{ $t('components.import.ImportPanel.geojson.importNameLabel') }}</label>
                <div class="AdminFormSection__Field">
                  <b-form-input
                    v-model="addressSourceLabel"
                    :maxlength="24"
                    :minlength="3"
                    :placeholder="$t('components.import.ImportPanel.geojson.importNamePlaceholder')"
                    :disabled="apis.geojson.loading"
                    @input="handleAddressLabel"
                  />
                  <b-spinner
                    v-if="isCheckingSourceExistance"
                    small
                    variant="secondary"
                    class="absolute-right"
                  />
                  <small
                    v-if="isExistingSource"
                    class="text-danger d-block mt-1"
                  >{{ $t('components.import.ImportPanel.existingSource', { label: addressSourceLabel }) }}</small>
                  <small
                    v-else
                    class="text-muted d-block mt-1"
                  >{{ $t('components.import.ImportPanel.geojson.importNameHelp') }}</small>
                </div>
              </div>

              <!-- Name in comments with preview button -->
              <div class="AdminFormSection__Row mb-2">
                <label class="AdminFormSection__Label geojson">{{ $t('components.import.ImportPanel.geojson.nameInCommentsLabel') }}</label>
                <div class="AdminFormSection__Field">
                  <b-form-input
                    v-model="nameInComments"
                    :placeholder="$t('components.import.ImportPanel.geojson.nameInCommentsPlaceholder')"
                    :disabled="apis.geojson.loading"
                  />
                  <small class="text-muted d-block mt-1">{{ $t('components.import.ImportPanel.geojson.nameInCommentsHelp') }}</small>
                </div>
                <div class="AdminFormSection__Actions geojson">
                  <b-button
                    variant="secondary"
                    size="sm"
                    :disabled="apis.geojson.loading"
                    @click="previewNameInComments"
                  >
                    {{ $t('components.import.ImportPanel.geojson.previewButton') }}
                  </b-button>
                </div>
              </div>

              <!-- Status, file chooser and upload -->
              <div class="AdminFormSection__Row">
                <label class="AdminFormSection__Label">
                  {{ $t('components.import.ImportPanel.status.title') }}
                  <span v-if="apis.geojson.loading"> {{ $t('components.import.ImportPanel.status.loading') }} </span>
                  <span
                    v-else-if="apis.geojson.success"
                    class="text-success"
                  > {{ $t('components.import.ImportPanel.status.importSucceeded') }} </span>
                  <span
                    v-else-if="apis.geojson.error"
                    class="text-danger"
                  > {{ $t('components.import.ImportPanel.status.error') }}<template v-if="apis.geojson.errorMessage">: {{ apis.geojson.errorMessage }}</template> </span>
                  <span v-else> {{ $t('components.import.ImportPanel.status.notStarted') }} </span>
                </label>
                <div class="AdminFormSection__Field">
                  <b-form-file
                    v-model="geojsonFile"
                    accept=".geojson, .json"
                    :placeholder="$t('dragNselect')"
                    :drop-placeholder="$t('drag')"
                    :disabled="apis.geojson.loading"
                  />
                </div>
                <div class="AdminFormSection__Actions">
                  <b-button
                    variant="cta"
                    :disabled="isExistingSource || apis.geojson.loading || !geojsonFile || !isValidImportName"
                    @click="handleImportGeoJSON"
                  >
                    {{ dryRun ? $t('components.upload.UploadPanel.uploadTest') : $t('components.upload.UploadPanel.upload') }}
                  </b-button>
                </div>
              </div>

              <!-- Download example link -->
              <div class="AdminFormSection__Row AdminFormSection__Row--footer">
                <label class="AdminFormSection__Label" />
                <div class="AdminFormSection__Field">
                  <a
                    class="AdminFormSection__Download"
                    href="/examples/geojson-chargingpoints-example.geojson"
                    download
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $t('components.import.ImportPanel.geojson.downloadExample') }}
                  </a>
                </div>
                <div class="AdminFormSection__Actions" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-else>
        <p>
          {{ $t('components.import.ImportPanel.noAPIs') }}
        </p>
      </section>
    </div>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'

import { checkStatus } from '@/helpers/api'

import ToggleSwitch from '@/components/form/ToggleSwitch'

// use shared comment composer to generate preview text
import { composeCommentNewChargingpointByImport } from '../../../shared/services/comment-composer'

export default {
  name: 'ImportPanel',
  components: { ToggleSwitch },
  data() {
    return {
      dryRun: true,
      addressSourceLabel: '',
      nameInComments: '',
      geojsonFile: null,
      apis: {
        geojson: {
          success: false,
          error: false,
          loading: false,
          errorMessage: '',
        },
      },
      debounceTimeout: null,
      token: '',
      isCheckingSourceExistance: false,
      isExistingSource: false,
    }
  },
  computed: {
    ...mapGetters('access', ['getActiveMunicipality']),
    ...mapGetters('deployment', ['DeploymentCode']),
    isValidImportName() {
      return !!this.addressSourceLabel && this.addressSourceLabel.length > 0
    },
  },
  watch: {},
  async created() {
    this.token = await this.$auth.getTokenSilently()
  },
  methods: {
    handleAddressLabel (event) {
      this.isExistingSource = false
      if (event && event.length < 3 ) return

      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(async () => {
        this.isCheckingSourceExistance = true

        const api = await fetch('/api/evtools-import-chargingpoints', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: 'Bearer ' + this.token,
          },
          body: JSON.stringify({
            dataSourceExists: true,
            label: event,
          }),
        }).catch(err => console.log(err))

        const response = await api.json()
        // Trigger source exist error message //
        this.isExistingSource = !!response.error
        this.isCheckingSourceExistance = false

      }, 1000)
    },
    autoRefreshOnSuccess(timeout = 3000) {
      // Auto-refresh the page after a short delay when an import has successfully completed
      // Only refresh when not in dry run mode
      try {
        if (!this.dryRun) {
          this.$notify({
            type: 'success',
            title: this.$t('autoRefresh.title'),
            text: this.$t('autoRefresh.message'),
          })

          setTimeout(() => {
            if (typeof window !== 'undefined' && window.location) {
              window.location.reload()
            } else if (this.$router && this.$router.go) {
              this.$router.go(0)
            }
          }, timeout)
        }
      } catch (e) {
        // fail silently; refresh is non-critical
      }
    },
    previewNameInComments() {
      try {
        const comment = composeCommentNewChargingpointByImport({
          chargingpoint: { code: '----', uuid: '00000000-0000-0000-0000-000000000000' },
          name: this.nameInComments,
        })
        const message = comment && (comment.Message || comment.message) || ''
        if (this.$bvModal && this.$bvModal.msgBoxOk) {
          this.$bvModal.msgBoxOk(message, {
            title: this.$t('components.import.ImportPanel.geojson.previewTitle'),
            okTitle: 'OK',
            size: 'sm',
          })
        } else {
          // fallback
          alert(message)
        }
      } catch (e) {
        // fallback
        alert(this.$t('components.import.ImportPanel.geojson.previewError'))
      }
    },
    async handleImportGeoJSON() {
      if (!this.geojsonFile) return
      this.apis.geojson.success = false
      this.apis.geojson.error = false
      this.apis.geojson.errorMessage = ''
      this.apis.geojson.loading = true
      try {
        const token = await this.$auth.getTokenSilently()
        const text = await this.geojsonFile.text()
        let data
        try {
          data = JSON.parse(text)
        } catch (parseErr) {
          throw new Error('Invalid JSON file: cannot parse')
        }
        if (!data || data.type !== 'FeatureCollection' || !Array.isArray(data.features)) {
          throw new Error('Invalid GeoJSON: expected FeatureCollection with features')
        }
        // Optional: ensure points
        const hasNonPoint = data.features.some(f => f && f.geometry && f.geometry.type && f.geometry.type !== 'Point')
        if (hasNonPoint) {
          // Not fatal, but informatively continue; if needed, this could be a strict error
          // throw new Error('GeoJSON must contain only Point features')
        }

        await fetch('/api/evtools-import-chargingpoints', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: 'Bearer ' + token,
          },
          body: JSON.stringify({
            DRY_RUN: this.dryRun,
            nameInComments: this.nameInComments,
            addressSourceLabel: this.addressSourceLabel,
            data,
          }),
        })
          .then(await checkStatus)

        this.apis.geojson.success = true
        this.autoRefreshOnSuccess()
      } catch (e) {
        console.error('error in GeoJSON import:', e)
        this.apis.geojson.error = true
          this.apis.geojson.errorMessage = e.message || e.toString()
      } finally {
        this.apis.geojson.loading = false
      }
    },
  },
}
</script>

<style lang="scss">


/**
 * This component combines the html & css of three layered admin components,
 *  because it doesn't need any of the logic of those components
 * TODO: Fix html & CSS so that it makes sense as one component
 */
.UploadDashboard {
  &__Panel, &__Select {
    // width: calc(100% - 2rem);
    min-width: 1100px;
    max-width: 1100px;
  }

  &__Panel {
    // border: 1px solid red;
    background: white;
    box-shadow: 0px 1px 3px 0px #dfe2e5;
  }

  .vSelect {
    min-width: 300px;
  }
}

.ImportPanelWrapper {

  min-width: 1100px;
  max-width: 1100px;
  background: white;
  box-shadow: 0px 1px 3px 0px #dfe2e5;

  header {
    background: var(--primary);
    color: white;
    font-size: 1.5rem;
  }

  form {
    label {
      font-size: 1.1rem;
    }

    small {
      outline: none !important;
      font-size: 1rem;
    }
  }
}

.ImportPanel {
  &__Aside {
    width: 275px;
  }

  &__Divider {
    border-top: solid 1px lightgray;
    margin: 1.5rem 2rem 0 2rem;
  }

  &__Main {
    font-size: 1.25rem;

    .form-control {
      width: 100%;
      font-size: 1.25rem;
    }

    .custom-file { width: 100%; }
    .custom-file-label {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__Row {
    display: grid;
    grid-template-columns: 180px 1fr auto;
    align-items: center;
    column-gap: 1rem;
  }

  &__Input {
    position: relative;
    width: 100%;

    &:after {
      content: attr(data-intructions);
      position: absolute;
      top: 3.1rem;
      font-size: 1rem;
      left: 0;
      color: #7F8FA4;

    }
  }

  &__Download {
    text-decoration: underline;
    color: var(--cta);
  }

  .SvgIcon {
    flex-shrink: 0;
    font-size: 1.75rem;
  }

  .custom-file-input:lang(en) ~ .custom-file-label::after {
    content: 'Selecteer';
  }
}

/* Generic admin form section styles (reusable) */
.AdminFormSection {
  padding: 0 0.5rem;
  &__Actions {
    &.geojson {
      align-self: flex-start;
      margin-top: 5px;
    }
  }
}
.AdminFormSection__Header {
  margin-bottom: 0.75rem;
}
.AdminFormSection__Title {
  margin: 0 0 0.25rem 0;
}
.AdminFormSection__Description {
  margin: 0;
  color: #6c757d;
  font-size: 1rem;
}
.AdminFormSection__Body {
  display: flex;
  flex-direction: column;
  row-gap: 0.75rem;
}
.AdminFormSection__Row {
  display: grid;
  grid-template-columns: 220px 1fr auto;
  align-items: center;
  column-gap: 1rem;
}
.AdminFormSection__Row--footer {
  align-items: start;
}
.AdminFormSection__Label {
  font-weight: 600;
  color: #394451;
  &.geojson {
    align-self: flex-start;
    margin-top: 5px;
  }
}
.AdminFormSection__Field {
  min-width: 0; /* allow shrinking */
  position: relative;
}
.AdminFormSection__Download {
  text-decoration: underline;
  color: var(--cta);
}
.AdminFormSection .form-control {
  font-size: 1.1rem;
}
.AdminFormSection .custom-file { width: 100%; }
.AdminFormSection .custom-file-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.AdminFormSection .btn { white-space: nowrap; }
.absolute-right {
  position: absolute;
  top: 11px;
  right: 1em;
}
</style>
